import { useSearchParams } from 'react-router-dom';

const QUERY_KEY = 'q';

type ContactUrlFilters = {
  query: string;
  openContactId: string | null;
  setQuery: (value: string) => void;
  setOpenContactId: (value: string | null) => void;
};

function decodeFilters(searchParams: URLSearchParams) {
  const filters: Record<string, string> = {};
  const encoded = searchParams.get(QUERY_KEY);
  if (!encoded) return filters;

  let decoded = '';
  try {
    decoded = atob(encoded);
  } catch {
    console.error('Something went wrong parsing the url');
    return filters;
  }

  decoded.split('&').forEach((pair) => {
    const [key, value = ''] = pair.split('=');
    if (key && value) filters[key] = value;
  });

  return filters;
}

function encodeFilters(filters: Record<string, string>) {
  const entries = Object.entries(filters).filter(([, value]) => !!value);
  if (!entries.length) return '';
  return btoa(entries.map(([key, value]) => `${key}=${value}`).join('&'));
}

/**
 * Reads/writes the `query` and `openContactId` filters that live inside the
 * base64-encoded `q` search param, without disturbing other search params
 * (sourceTypes, countryCode, tags) that live alongside it in the URL.
 */
export function useContactUrlFilters(): ContactUrlFilters {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = decodeFilters(searchParams);

  function updateFilters(patch: Record<string, string | null>) {
    setSearchParams((prev) => {
      const currentFilters = decodeFilters(prev);
      Object.entries(patch).forEach(([key, value]) => {
        if (value === null) {
          delete currentFilters[key];
        } else {
          currentFilters[key] = value;
        }
      });

      const next = new URLSearchParams(prev);
      const encoded = encodeFilters(currentFilters);
      if (encoded) {
        next.set(QUERY_KEY, encoded);
      } else {
        next.delete(QUERY_KEY);
      }
      return next;
    });
  }

  return {
    query: filters.query ?? '',
    openContactId: filters.openContactId ?? null,
    setQuery: (value) => updateFilters({ query: value }),
    setOpenContactId: (value) => updateFilters({ openContactId: value }),
  };
}
